// Manage Projects modal — clean, flat, company-free (R32: the Company layer is hidden).
//   • Active tab: a flat list of projects; each row is name + Active pill + "Updated …"
//     + a single ⋮ menu (Rename / Archive / Delete). Click the name to rename inline.
//   • Archived tab: reactivate or delete.
// Per-project color chip gives identity without exposing the Company concept.
import React, { useState } from "react";
import { X, Check, Pencil, Archive, RotateCcw, Trash2, Loader2, AlertTriangle, Search, Folders, FileText, ChevronRight, ChevronDown, Plus, MoreVertical } from "lucide-react";
import { useProjects } from "../ProjectContext";
import { getFreshToken } from "../api";
import CompanyAvatar from "./CompanyAvatar";
import NewProjectModal from "./NewProjectModal";
import { friendlyFileName } from "../utils/fileName";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");

export default function ManageProjectsModal({ open, onClose }) {
  const {
    projects, loading,
    renameProjectById, archive, reactivate, remove, refresh,
  } = useProjects();

  const [archived, setArchived] = useState([]);
  const [tab, setTab] = useState("active");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);          // {id, value}
  const [confirmDel, setConfirmDel] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);   // projectId whose ⋮ menu is open
  const [openFiles, setOpenFiles] = useState(null); // projectId whose file list is expanded
  const [filesCache, setFilesCache] = useState({}); // {projectId: {loading, files, error}}
  const [newOpen, setNewOpen] = useState(false);    // "New project" dialog (create lives inside Manage)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Lazy-load a project's files (view-only) when its row is expanded. Fetches with an
  // explicit X-Project-Id header for THAT project (the fetch wrapper leaves it as-is).
  const loadProjectFiles = async (pid) => {
    setFilesCache((c) => ({ ...c, [pid]: { loading: true } }));
    try {
      const t = await getFreshToken();
      const res = await fetch(`${API_URL}/upload/files`, { headers: { Authorization: `Bearer ${t}`, "X-Project-Id": pid } });
      const data = res.ok ? await res.json() : {};
      setFilesCache((c) => ({ ...c, [pid]: { loading: false, files: data.files || [] } }));
    } catch (e) {
      setFilesCache((c) => ({ ...c, [pid]: { loading: false, error: true } }));
    }
  };
  const toggleFiles = (pid) => {
    const next = openFiles === pid ? null : pid;
    setOpenFiles(next);
    if (next && !filesCache[pid]) loadProjectFiles(pid);
  };

  // Close the row actions menu on any outside click.
  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = () => setMenuOpen(null);
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [menuOpen]);

  React.useEffect(() => {
    if (!open) return;
    setErr(null);
    import("../api").then(({ listProjects }) =>
      listProjects("archived").then((r) => setArchived(r.projects || [])).catch(() => {})
    );
  }, [open, projects]);

  if (!open) return null;

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    if (isNaN(diff)) return "";
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return d === 1 ? "yesterday" : `${d}d ago`;
  };
  const matchesQuery = (p) => {
    const s = q.trim().toLowerCase();
    return !s || p.displayName.toLowerCase().includes(s);
  };

  const run = async (fn) => {
    setBusy(true); setErr(null);
    try { await fn(); }
    catch (e) { setErr(e?.response?.data?.detail || "That action couldn't be completed."); }
    finally { setBusy(false); }
  };
  const saveEdit = () => {
    if (!edit || !edit.value.trim()) return;
    const { id, value } = edit;
    run(async () => { await renameProjectById(id, value.trim()); setEdit(null); });
  };

  const tabBtn = (key, label) => (
    <button onClick={() => { setTab(key); setQ(""); }} style={{
      padding: "7px 14px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "13px", fontWeight: 700,
      background: tab === key ? "#eef2ff" : "transparent", color: tab === key ? "#4f46e5" : "#64748b",
    }}>{label}</button>
  );

  const ActionBtn = ({ icon, label, onClick, danger }) => (
    <button onClick={onClick} disabled={busy} title={label} style={{
      display: "inline-flex", alignItems: "center", gap: "5px", padding: "6px 9px", borderRadius: "8px",
      border: "1px solid " + (danger ? "#fecaca" : "#e2e8f0"), background: danger ? "#fef2f2" : "#fff",
      color: danger ? "#dc2626" : "#475569", cursor: busy ? "not-allowed" : "pointer", fontSize: "12px", fontWeight: 600,
    }}>{icon}{label}</button>
  );

  // A single row in the ⋮ action menu (lean SaaS style; Delete gets the danger treatment).
  const MenuItem = ({ icon, label, onClick, danger }) => (
    <button onClick={onClick} disabled={busy} style={{
      width: "100%", display: "flex", alignItems: "center", gap: "8px", padding: "7px 9px", borderRadius: "7px",
      border: "none", background: "transparent", cursor: busy ? "not-allowed" : "pointer", fontSize: "13px",
      fontWeight: 500, color: danger ? "#dc2626" : "#334155", textAlign: "left",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.background = danger ? "#fef2f2" : "#f8fafc"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
      {icon}{label}
    </button>
  );

  const inputStyle = { border: "1px solid #c7d2fe", borderRadius: "7px", padding: "6px 9px", fontSize: "13px", outline: "none" };

  const projectRow = (p) => {
    return (
      <div key={p.projectId} style={{ marginBottom: "1px" }}>
        <div
          onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 8px", borderRadius: "8px" }}>
          {/* Expand to view this project's files (read-only) */}
          <button onClick={() => toggleFiles(p.projectId)} title="View files in this project"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "inline-flex", padding: 0, width: "14px", flexShrink: 0 }}>
            {openFiles === p.projectId ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          <CompanyAvatar companyId={p.projectId} name={p.displayName} size={22} />
          {edit && edit.id === p.projectId ? (
            <>
              <input autoFocus value={edit.value} onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEdit(null); }}
                style={{ ...inputStyle, flex: 1 }} />
              <ActionBtn icon={<Check size={14} />} label="Save" onClick={saveEdit} />
              <button onClick={() => setEdit(null)} title="Cancel" style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", flexShrink: 0 }}><X size={16} /></button>
            </>
          ) : (
            <>
              {/* Name + lazy file-count subtitle. The whole area is click-to-expand (files);
                  rename now lives in the ⋮ menu. No ACTIVE pill — we're already in Active. */}
              <div onClick={() => toggleFiles(p.projectId)} title={p.displayName}
                style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "1px", cursor: "pointer" }}>
                <span style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.displayName}</span>
                {(() => {
                  const fc = filesCache[p.projectId];
                  if (!fc || !fc.files) return null;
                  const n = fc.files.length;
                  return <span style={{ fontSize: "11px", color: "#94a3b8" }}>{n} file{n !== 1 ? "s" : ""} in this project</span>;
                })()}
              </div>
              {(p.updatedAt || p.lastUploadAt) && (
                <span style={{ fontSize: "11px", color: "#94a3b8", flexShrink: 0, whiteSpace: "nowrap" }}>
                  Updated {timeAgo(p.updatedAt || p.lastUploadAt)}
                </span>
              )}
              {/* Single ⋮ action menu (Rename / Archive / — / Delete) — replaces the row of
                  inline buttons to cut visual noise. */}
              <div style={{ position: "relative", flexShrink: 0 }}>
                <button onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === p.projectId ? null : p.projectId); }}
                  title="Actions" style={{
                    padding: "5px", borderRadius: "7px", border: "none", display: "inline-flex",
                    background: menuOpen === p.projectId ? "#eef2ff" : "transparent",
                    color: "#64748b", cursor: "pointer",
                  }}><MoreVertical size={16} /></button>
                {menuOpen === p.projectId && (
                  <div onClick={(e) => e.stopPropagation()} style={{
                    position: "absolute", right: 0, top: "32px", width: "156px", background: "#fff",
                    border: "1px solid #e2e8f0", borderRadius: "10px", boxShadow: "0 10px 30px rgba(15,23,42,0.14)",
                    padding: "5px", zIndex: 30,
                  }}>
                    <MenuItem icon={<Pencil size={13} />} label="Rename"
                      onClick={() => { setMenuOpen(null); setEdit({ id: p.projectId, value: p.displayName }); }} />
                    <MenuItem icon={<Archive size={13} />} label="Archive"
                      onClick={() => { setMenuOpen(null); run(() => archive(p.projectId)); }} />
                    {/* The Default ("Main") Project can't be deleted — it's the always-present
                        home for data (the server also enforces this). Hide Delete for it. */}
                    {p.projectId !== "default" && (
                      <>
                        <div style={{ height: "1px", background: "#f1f5f9", margin: "4px 6px" }} />
                        <MenuItem icon={<Trash2 size={13} />} label="Delete" danger
                          onClick={() => { setMenuOpen(null); setConfirmDel(p.projectId); }} />
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Files in this project — read-only view (lazy-loaded on expand) */}
        {openFiles === p.projectId && (() => {
          const fc = filesCache[p.projectId] || {};
          return (
            <div style={{ margin: "2px 0 8px 40px", padding: "10px 12px", background: "#f8fafc", border: "1px solid #eef1f6", borderRadius: "8px" }}>
              {fc.loading ? (
                <div style={{ fontSize: "12.5px", color: "#94a3b8", display: "inline-flex", alignItems: "center", gap: "6px" }}><Loader2 size={13} className="animate-spin" /> Loading files…</div>
              ) : fc.error ? (
                <div style={{ fontSize: "12.5px", color: "#94a3b8" }}>Couldn't load files.</div>
              ) : (fc.files && fc.files.length) ? (
                <>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", marginBottom: "6px" }}>
                    {fc.files.length} file{fc.files.length !== 1 ? "s" : ""} in this project
                  </div>
                  {fc.files.map((f) => (
                    <div key={f.key} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "3px 0", fontSize: "12.5px", color: "#334155", minWidth: 0 }}>
                      <FileText size={13} style={{ color: "#94a3b8", flexShrink: 0 }} />
                      {f.category && <span style={{ background: "#eef2ff", color: "#4f46e5", padding: "1px 6px", borderRadius: "4px", fontSize: "10px", fontWeight: 600, flexShrink: 0 }}>{f.category}</span>}
                      <span title={friendlyFileName(f.file_name)} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{friendlyFileName(f.file_name)}</span>
                    </div>
                  ))}
                  <div style={{ fontSize: "10.5px", color: "#94a3b8", marginTop: "7px" }}>View only — upload, move, or archive files from the File Upload page.</div>
                </>
              ) : (
                <div style={{ fontSize: "12.5px", color: "#94a3b8" }}>No files in this project yet.</div>
              )}
            </div>
          );
        })()}

      </div>
    );
  };

  const activeList = projects.filter(matchesQuery);
  const delProj = confirmDel ? [...projects, ...archived].find((p) => p.projectId === confirmDel) : null;

  return (
    <>
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 100001, background: "rgba(8,12,24,0.55)",
      backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: "16px", width: "100%", maxWidth: "620px", maxHeight: "86vh",
        display: "flex", flexDirection: "column", boxShadow: "0 24px 70px rgba(0,0,0,0.32)", overflow: "hidden",
      }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #eef1f6", display: "flex", alignItems: "center", gap: "11px" }}>
          <div style={{ width: "30px", height: "30px", borderRadius: "8px", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Folders size={16} color="#fff" />
          </div>
          <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#0f172a", flex: 1 }}>Manage Projects</h3>
          <div style={{ display: "flex", gap: "2px" }}>
            {tabBtn("active", "Active")}
            {tabBtn("archived", "Archived")}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={18} /></button>
        </div>

        <div style={{ padding: "16px 22px", overflowY: "auto" }}>
          {err && <div style={{ fontSize: "12.5px", color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "8px 10px", marginBottom: "12px" }}>{err}</div>}

          <div style={{ display: "flex", gap: "8px", margin: "0 0 14px" }}>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "8px", padding: "8px 11px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "9px" }}>
              <Search size={14} style={{ color: "#94a3b8" }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects"
                style={{ border: "none", outline: "none", background: "transparent", fontSize: "13px", width: "100%", color: "#0f172a" }} />
            </div>
            {/* Create lives inside Manage now — one project hub. Ultra-lean indigo pill CTA. */}
            <button onClick={() => setNewOpen(true)} title="Create a new project"
              onMouseEnter={(e) => { e.currentTarget.style.background = "#e0e7ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#eef2ff"; }}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px",
                height: "38px", padding: "0 14px", borderRadius: "999px", border: "none",
                background: "#eef2ff", color: "#4f46e5", fontWeight: 600, fontSize: "13px",
                cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, transition: "background .15s",
              }}><Plus size={14} strokeWidth={2.5} /> New project</button>
          </div>

          {/* ACTIVE — flat project list */}
          {tab === "active" && (loading ? <p style={{ color: "#94a3b8", fontSize: "13px" }}>Loading…</p> :
            projects.length === 0 ? <p style={{ color: "#94a3b8", fontSize: "13px" }}>No active projects yet.</p> :
            activeList.length === 0 ? <p style={{ color: "#94a3b8", fontSize: "13px" }}>No projects match your search.</p> :
            activeList.map(projectRow)
          )}

          {/* ARCHIVED */}
          {tab === "archived" && (archived.length === 0 ? <p style={{ color: "#94a3b8", fontSize: "13px" }}>No archived projects.</p> :
            archived.filter(matchesQuery).length === 0 ? <p style={{ color: "#94a3b8", fontSize: "13px" }}>No archived projects match your search.</p> :
            archived.filter(matchesQuery).map((p) => (
              <div key={p.projectId}>
                <div style={{ display: "flex", alignItems: "center", gap: "11px", border: "1px solid #eef1f6", borderRadius: "12px", padding: "12px 14px", marginBottom: confirmDel === p.projectId ? "0" : "10px" }}>
                  <CompanyAvatar companyId={p.projectId} name={p.displayName} size={24} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.displayName}</div>
                    <div style={{ fontSize: "11px", color: "#94a3b8" }}>archived</div>
                  </div>
                  <ActionBtn icon={<RotateCcw size={13} />} label="Reactivate" onClick={() => run(async () => { await reactivate(p.projectId); await refresh(); setArchived(archived.filter(a => a.projectId !== p.projectId)); })} />
                  {/* Delete → centered confirmation dialog (shared safety guardrail). */}
                  <ActionBtn icon={<Trash2 size={13} />} label="Delete" danger onClick={() => setConfirmDel(p.projectId)} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
    {/* Create lives inside Manage — rendered as a sibling so its clicks don't close Manage */}
    <NewProjectModal open={newOpen} onClose={() => setNewOpen(false)} />

    {/* Destructive-action safety guardrail — a centered confirmation dialog (R: Delete). */}
    {confirmDel && (
      <div onClick={() => !busy && setConfirmDel(null)} style={{
        position: "fixed", inset: 0, zIndex: 100002, background: "rgba(8,12,24,0.5)",
        backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
      }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          background: "#fff", borderRadius: "16px", width: "100%", maxWidth: "420px", padding: "20px",
          boxShadow: "0 24px 70px rgba(0,0,0,0.32)",
        }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", marginBottom: "8px" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "999px", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <AlertTriangle size={18} color="#dc2626" />
            </div>
            <div>
              <h4 style={{ margin: "2px 0 4px", fontSize: "15px", fontWeight: 700, color: "#0f172a" }}>Delete project?</h4>
              <p style={{ margin: 0, fontSize: "13px", color: "#64748b", lineHeight: 1.55 }}>
                Permanently delete <strong style={{ color: "#334155" }}>{delProj ? delProj.displayName : "this project"}</strong>?
                This removes its <strong>files, cleaned data, schema, and chat history</strong> and can't be
                undone — export anything you need first.
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "16px" }}>
            <button onClick={() => setConfirmDel(null)} disabled={busy} style={{ padding: "8px 14px", borderRadius: "9px", border: "1px solid #e2e8f0", background: "#fff", color: "#475569", cursor: busy ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}>Cancel</button>
            <button onClick={() => run(async () => { const id = confirmDel; await remove(id); setConfirmDel(null); setArchived(archived.filter((a) => a.projectId !== id)); })} disabled={busy}
              style={{ padding: "8px 14px", borderRadius: "9px", border: "none", background: "#dc2626", color: "#fff", cursor: busy ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: "6px" }}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
